/** One course: enroll CTA, resume banner, chapters with lesson rows.
 *
 * A course nobody has bought does not 403 any more — it answers with its
 * header and the ONE free lesson (see free_taster.py). That is the taster,
 * and this page is where a learner meets it: the free tile is badged and
 * openable, everything else is replaced by the offer. Enrolling is hidden
 * there, because enrolling is not a way in and the server would refuse it.
 */
import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ApiError } from "../../api/http";
import * as api from "../../api/learning";
import { useToast } from "../../components/ToastContext";
import { Badge, Button, Card, ProgressRing, Spinner } from "../../components/ui";
import { FreeBadge, LockedNotice } from "../billing/LockedNotice";
import { useT } from "../../i18n";

export function CourseDetailPage() {
  const t = useT();
  const { push } = useToast();
  const { courseId = "" } = useParams();
  const [tree, setTree] = useState<api.CourseTree | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      setTree(await api.courseTree(courseId));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "error");
    }
  }, [courseId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const onEnroll = async () => {
    try {
      await api.enrollInCourse(courseId);
      await reload();
    } catch (e) {
      push("error", e instanceof ApiError ? e.message : t("common.error.generic"));
    }
  };

  if (error) {
    return (
      <Card>
        <p role="alert" className="text-sm text-danger">{error}</p>
      </Card>
    );
  }
  if (!tree) {
    return (
      <div className="grid place-items-center py-12">
        <Spinner size={28} />
      </div>
    );
  }

  const next = tree.enrolled ? api.nextLesson(tree) : null;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <Badge tone="brand">{tree.level_code}</Badge>
          <h1 className="mt-2 text-3xl font-extrabold">{tree.title}</h1>
          {tree.description && <p className="mt-1 text-muted">{tree.description}</p>}
        </div>
        {tree.progress && (
          <ProgressRing
            percent={tree.progress.mastery_percent}
            label={t("learn.mastery")}
          />
        )}
      </div>

      {tree.taster ? null : !tree.enrolled ? (
        <Card>
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted">{t("learn.subtitle")}</p>
            <Button onClick={onEnroll}>{t("learn.enroll")}</Button>
          </div>
        </Card>
      ) : next ? (
        <Card>
          <div className="flex items-center justify-between border-l-4 border-brand pl-4">
            <div>
              <p className="text-xs uppercase text-muted">{t("learn.continue")}</p>
              <p className="mt-1 font-bold text-ink">{next.title}</p>
            </div>
            <Link to={`/learn/lessons/${next.id}`}>
              <Button>{t("learn.resume")} →</Button>
            </Link>
          </div>
        </Card>
      ) : (
        <Card>
          <p role="status" className="text-sm font-semibold text-success">
            🎉 {t("learn.completed")}
          </p>
        </Card>
      )}

      {tree.chapters.map((chapter) => (
        <Card key={chapter.id} title={chapter.title}>
          <ul className="divide-y divide-line/60">
            {chapter.lessons.map((lesson) => (
              <li key={lesson.id} className="flex items-center justify-between py-3">
                <div className="flex items-center gap-3">
                  <span aria-hidden>{lesson.completed ? "✅" : "📘"}</span>
                  <div>
                    <p className="flex flex-wrap items-center gap-2 text-sm font-semibold text-ink">
                      {lesson.title}
                      {lesson.is_free && <FreeBadge />}
                    </p>
                    <p className="text-xs text-muted">
                      +{lesson.xp_reward} {t("learn.xp")}
                      {lesson.theme && <> · {lesson.theme}</>}
                    </p>
                  </div>
                </div>
                {(tree.enrolled || lesson.is_free) && (
                  <Link to={`/learn/lessons/${lesson.id}`}>
                    <Button variant={lesson.completed ? "ghost" : "secondary"}>
                      {lesson.completed ? t("learn.review") : t("learn.start")}
                    </Button>
                  </Link>
                )}
              </li>
            ))}
          </ul>
        </Card>
      ))}

      {tree.taster && <LockedNotice />}
    </div>
  );
}
